import { ref, computed } from "vue";
import { defineStore } from "pinia";
import {
  getSchedule,
  getProductionCalendar,
  getPositions,
  getDayTypes,
  getShiftSchedule,
  getShiftHistory,
  removeShift,
  getTimeSheet,
  createUpdateShiftPack,
  getTimesheetReport,
  getConsolidatedReport,
  getScheduleReport,
  getDisciplineReport,
  getFraudReport,
  validateShift,
  cleanSchedule,
  generateSchedule,
  getStats,
} from "../api/schedule";
import { getEmploymentSchedule } from "@/api/users";
import type { ShiftCopyRequest } from "@/api/schedule";
import {
  Schedule,
  Position,
  DayType,
  Shift,
  Department,
  ScheduleRules,
} from "@/types";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import ru from "dayjs/locale/ru";
import { useToast } from "vue-toast-notification";
import { useDepartmentStore } from "./departmentStore";
import { useAuthStore } from "./authStore";

dayjs.locale(ru);
dayjs.extend(utc);

const toast = useToast({ position: "top" });

interface RuleCheckContext {
  department: string | undefined;
  month: string;
  year: string;
}

export const useScheduleStore = defineStore("schedule", () => {
  // Состояния
  const schedules = ref<Schedule[]>([]);
  const rules = ref<ScheduleRules | null>(null);
  const timesheets = ref<any[]>([]);
  const stats = ref();
  const schedulesSortedByName = ref<Schedule[]>([]);
  const schedulesSortedByPosition = ref<Schedule[]>([]);
  const prodCalendar = ref<any[]>([]);
  const positions = ref<Position[]>([]);
  const dayTypes = ref<DayType[]>([]);
  const shiftSchedules = ref<any[]>([]);
  const selectedEmploymentSchedule = ref<any>(null);
  const shiftHistory = ref<any[]>([]);
  const currentShift = ref<Shift | null>(null);
  const currentShifts = ref<Shift[]>([]);
  const originalShifts = ref<Shift[]>([]); // оригинальные сохранённые смены для отслеживания изменений
  const changedShifts = ref<string[]>([]); // массив ID изменённых смен
  const isCurrentShiftChanged = ref(false)
  const currentScheduleDate = ref(dayjs());
  const currentDepartment = ref<Department>();
  const refresh = ref(0);
  const users = ref<any[]>([]);
  const loading = ref<boolean>(false);
  let scheduleRequest = 0;
  let backgroundScheduleRequest = 0;
  let rulesRequest = 0;
  const isSaveShiftEnabled = ref(false);
  const shiftFieldsClicked = ref(false);
  const isShiftValid = ref<boolean>(false);
  const userStat = ref<any>(null);
  const userStat2 = ref<any>(null);
  const isLightTheme = ref(false);
  const isCellHighlighting = ref(false);
  const isDayStatsVisible = ref(false);
  const isEmployeeStatsVisible = ref(false);

  const departmentStore = useDepartmentStore();
  const authStore = useAuthStore();

  // Вычисляемые свойства
  const currentMonthDays = computed(() =>
    currentScheduleDate.value.daysInMonth(),
  );
  const currentMonthStart = computed(() =>
    currentScheduleDate.value.date(1).format("YYYY-MM-DD"),
  );
  const currentMonthEnd = computed(() =>
    currentScheduleDate.value.date(currentMonthDays.value).format("YYYY-MM-DD"),
  );
  const isNewShiftsExist = computed(() =>
    currentShifts.value.find((shift) => !shift.id),
  );

  // Функции для работы с графиками
  async function fetchStats() {
    const data = {
      department: currentDepartment.value?.id,
      month: currentScheduleDate.value.month() + 1,
      year: currentScheduleDate.value.year(),
    };
    try {
      const res = await getStats(data);
      stats.value = res.data.body;
    } catch (err: any) {
      return err;
    }
  }

  async function fetchSchedule(options: { checkRules?: boolean; background?: boolean } = {}) {
    if (options.background) return refreshScheduleInBackground(options);
    const params = {
      department: currentDepartment?.value?.id,
      start_date: currentMonthStart.value,
      end_date: currentMonthEnd.value,
    };
    if (currentDepartment.value && authStore.permissions?.can_view_schedule_and_timesheet) {
      const request = ++scheduleRequest;
      try {
        loading.value = true;
        fetchProductionCalendar();
        if (options.checkRules !== false) checkRules();
        await fetchStats();
        const res = await getSchedule(params);
        schedules.value = res.data.body;
        sortSchedules()
        refresh.value++;
      } catch (err: any) {
        return err;
      } finally {
        if (request === scheduleRequest) loading.value = false;
      }
    }
  }

  async function refreshScheduleInBackground(options: { checkRules?: boolean }) {
    if (!currentDepartment.value || !authStore.permissions?.can_view_schedule_and_timesheet) return;

    const context = currentRuleCheckContext();
    const request = ++backgroundScheduleRequest;
    const foregroundRequest = scheduleRequest;
    const params = {
      department: context.department,
      start_date: currentMonthStart.value,
      end_date: currentMonthEnd.value,
    };
    const results = await Promise.allSettled([
      getSchedule(params),
      options.checkRules === false ? Promise.resolve(undefined) : checkRules(context),
    ]);
    const isCurrentRequest = () => {
      const current = currentRuleCheckContext();
      return request === backgroundScheduleRequest && foregroundRequest === scheduleRequest &&
        current.department === context.department && current.month === context.month && current.year === context.year;
    };
    if (!isCurrentRequest()) return;

    // Показатели запрашиваем после обновления расписания и завершения checker.
    const [statsResult] = await Promise.allSettled([
      getStats({ department: context.department, month: Number(context.month), year: Number(context.year) }),
    ]);
    if (!isCurrentRequest()) return;

    const [scheduleResult, rulesResult] = results;
    const failed: string[] = [];
    if (scheduleResult.status === "fulfilled") {
      const updated: Schedule[] = scheduleResult.value.data.body;
      const byId = new Map(updated.map(schedule => [schedule.id, schedule]));
      const existingIds = new Set(schedules.value.map(schedule => schedule.id));
      schedules.value = [
        ...schedules.value.flatMap(schedule => {
          const replacement = byId.get(schedule.id);
          return replacement ? [replacement] : [];
        }),
        ...updated.filter(schedule => !existingIds.has(schedule.id)),
      ];
      sortSchedules();
    } else failed.push("расписание");
    if (statsResult.status === "fulfilled") stats.value = statsResult.value.data.body;
    else failed.push("показатели сотрудников");
    if (options.checkRules !== false && (rulesResult.status === "rejected" || !rulesResult.value)) {
      failed.push("проверки правил графика");
    }
    if (failed.length) toast.error(`Не удалось обновить: ${failed.join(", ")}. Повторите обновление графика.`);
  }

  function sortSchedules() {
    schedulesSortedByName.value = schedules.value
    const main = schedules.value.filter(item => !item.is_vacancy && !item.is_outsource)
    const outsource = schedules.value.filter(item => item.is_vacancy && item.is_outsource)
    const vacancy = schedules.value.filter(item => item.is_vacancy && !item.is_outsource)
    main.sort((a, b) => a.position.name.localeCompare(b.position.name))
    outsource.sort((a, b) => a.position.name.localeCompare(b.position.name))
    vacancy.sort((a, b) => a.position.name.localeCompare(b.position.name))
    schedulesSortedByPosition.value = [...main, ...outsource, ...vacancy]
  }



  async function fetchProductionCalendar() {
    const params = {
      start_date: currentMonthStart.value,
      end_date: currentMonthEnd.value,
    };
    if (currentDepartment.value) {
      try {
        const res = await getProductionCalendar(
          currentDepartment.value.id,
          params,
        );
        prodCalendar.value = res.data.body;
      } catch (err: any) {
        return err;
      }
    }
  }

  function currentRuleCheckContext(): RuleCheckContext {
    return {
      department: currentDepartment.value?.id,
      month: currentScheduleDate.value.format("MM"),
      year: currentScheduleDate.value.format("YYYY"),
    };
  }

  async function checkRules(context = currentRuleCheckContext()) {
    if (!context.department) return;

    const isCurrentContext = () => {
      const current = currentRuleCheckContext();
      return current.department === context.department &&
        current.month === context.month && current.year === context.year;
    };
    const request = isCurrentContext() ? ++rulesRequest : undefined;
    const shouldApply = () => request !== undefined && request === rulesRequest && isCurrentContext();
    const result = await departmentStore.fetchDepartmentRules(
      context.department,
      { month: context.month, year: context.year },
      shouldApply,
    );
    if (result && shouldApply()) rules.value = result;
    return result;
  }

  async function fetchTimeSheet() {
    const data = {
      year: currentScheduleDate.value.format("YYYY"),
      month: currentScheduleDate.value.format("M"),
      department_id: currentDepartment.value?.id,
      scope: "with_other_deps",
    };
    if (currentDepartment.value && authStore.permissions?.can_view_schedule_and_timesheet) {
      try {
        const res = await getTimeSheet(data);
        timesheets.value = res.data.body;
      } catch (err: any) {
        return err;
      }
    }
  }

  async function fetchEmploymentSchedule(id: string) {
    const params = {
      start_date: currentMonthStart.value,
      end_date: currentMonthEnd.value,
    };
    try {
      const res = await getEmploymentSchedule(id, params);
      selectedEmploymentSchedule.value = res.data.body;
      return res.data.body;
    } catch (err: any) {
      return err;
    }
  }

  async function scheduleCleanup(data: any) {
    try {
      loading.value = true;
      const res = await cleanSchedule(data);
      toast.success(res.data.msg);
      setTimeout(fetchSchedule, 1000);
    } catch (err: any) {
      loading.value = false;
      return err;
    }
  }

  async function scheduleGenerate() {
    const data = {
      department: currentDepartment.value?.id,
      year: currentScheduleDate.value.format("YYYY"),
      month: currentScheduleDate.value.format("MM"),
    };
    try {
      loading.value = true;
      const res = await generateSchedule(data);
      toast.success(res.data.msg);
      await fetchSchedule();
    } catch (err: any) {
      loading.value = false;
      return err;
    }
  }

  async function fetchPositions() {
    try {
      const res = await getPositions();
      return (positions.value = res.data.body);
    } catch (err: any) {
      return err;
    }
  }

  function getPosition(id: string) {
    return positions.value.find((item) => item.id === id);
  }

  async function fetchDayTypes() {
    try {
      const res = await getDayTypes();
      return (dayTypes.value = res.data.body);
    } catch (err: any) {
      return err;
    }
  }

  function getDayType(id: string) {
    return dayTypes.value.find((item) => item.id === id);
  }

  async function fetchShiftSchedules() {
    try {
      const res = await getShiftSchedule();
      return (shiftSchedules.value = res.data.body);
    } catch (err: any) {
      return err;
    }
  }

  async function fetchShiftHistory(id: string) {
    try {
      shiftHistory.value = [];
      const res = await getShiftHistory(id);
      return (shiftHistory.value = res.data.body);
    } catch (err: any) {
      return err;
    }
  }

  async function shiftValidate(shift: any) {
    try {
      const res = await validateShift(shift);
      if (res.data.status === "ok") {
        isShiftValid.value = res.data.body.is_valid;
        const errors = res.data.body.errors;
        if (!res.data.body.is_valid && errors.length) {
          errors.forEach((error: string) => {
            toast.error(error);
          });
        }
      }
    } catch (err: any) {
      return err;
    }
  }

  async function handleSaveButtonAvailability() {
    if(authStore.settings?.USE_42_HOURS_REST_CHECK || authStore.settings?.USE_HOURS_LIMIT_CHECK) {
        await shiftValidate(currentShift.value);
    } else {
        isShiftValid.value = true
    }
    
    isSaveShiftEnabled.value =
      (isNewShiftsExist.value ||
        !!changedShifts.value.length) &&
      isShiftValid.value
  }

  async function shiftDelete(shift: any) {
    try {
      const res = await removeShift(shift.id);
      if (res.data.status === "ok") {
        toast.success(res.data.msg);
        updateScheduleDelete(shift);
        await checkRules();
        return res.data.status;
      }
    } catch (err: any) {
      return err;
    }
  }

  async function shiftPackCreateUpdate(data: object, refreshStats = true) {
    try {
      const payload = buildShiftPackPayload(data);

      if (!payload.length) return [];

      const checks = savedShiftRuleChecks(payload, data);
      const res = await createUpdateShiftPack(payload);
      if (res.data.status === "ok") {
        toast.success(res.data.msg);
        updateSchedulePack(res.data.body);
        updateSavedShiftBaselines(payload);
        const [, warnings] = await Promise.all([
          refreshStats ? fetchStats() : Promise.resolve(),
          collectSavedShiftWarnings(checks),
        ]);
        if (warnings.length) toast.error(warnings.join("<br>"));
        return res.data.body;
      }
    } catch (err: any) {
      return err;
    }
  }

  function savedShiftRuleChecks(payload: Partial<Shift>[], data: object) {
    const sourceShifts: Partial<Shift>[] = Array.isArray(data) ? data : [data];
    const fallback = currentRuleCheckContext();
    const checks = new Map<string, { context: RuleCheckContext; employments: Set<string> }>();

    for (const saved of payload) {
      // Diff-payload существующей смены содержит только id и изменённые поля.
      const source = saved.id ? sourceShifts.find(shift => shift.id === saved.id) : saved;
      const shift = { ...source, ...saved };
      const date = shift.date ? dayjs(shift.date) : undefined;
      const context = {
        department: shift.department ?? fallback.department,
        month: date ? date.format("MM") : fallback.month,
        year: date ? date.format("YYYY") : fallback.year,
      };
      const key = `${context.department}/${context.year}/${context.month}`;
      let check = checks.get(key);
      if (!check) {
        check = { context, employments: new Set<string>() };
        checks.set(key, check);
      }
      if (shift.employment) check.employments.add(shift.employment);
    }
    return [...checks.values()];
  }

  async function checkCopiedShiftRules(operation: ShiftCopyRequest) {
    const months = new Map(operation.target_dates.map(date => [date.slice(0, 7), dayjs(date)]));
    const employments = new Set(operation.employment_ids);
    return collectSavedShiftWarnings([...months.values()].map(date => ({
      context: {
        department: operation.department_id,
        month: date.format("MM"),
        year: date.format("YYYY"),
      },
      employments,
    })), true);
  }

  async function collectSavedShiftWarnings(checks: ReturnType<typeof savedShiftRuleChecks>, reportFailedChecks = false): Promise<string[]> {
    const results = await Promise.all(checks.map(async ({ context, employments }) => ({
      result: await checkRules(context),
      employments,
    })));
    if (results.some(({ result }) => !result)) {
      if (reportFailedChecks) toast.error("Не удалось обновить проверки правил графика. Повторите обновление графика.");
      return [];
    }

    const warnings: string[] = [];
    if (results.some(({ result, employments }) =>
      result?.rule_42_hours?.employments?.some(item => employments.has(item.employment_id)))) {
      warnings.push("Нарушение правила 42 часов");
    }
    if (results.some(({ result, employments }) =>
      result?.hours_limit_rule?.employments?.some(item =>
        !item.is_valid && employments.has(item.employment_id)))) {
      warnings.push("Превышена норма плановых часов");
    }
    return warnings;
  }

  const editableShiftFields = [
    "plan_work_start_at",
    "plan_work_end_at",
    "fact_work_start_at",
    "fact_work_end_at",
    "day_type",
    "position",
    "is_overtime",
    "duration",
    "day_state_document",
  ];
  const dateTimeShiftFields = [
    "plan_work_start_at",
    "plan_work_end_at",
    "fact_work_start_at",
    "fact_work_end_at",
  ];

  function buildShiftPackPayload(data: any) {
    const shifts = Array.isArray(data) ? data : [data];

    return shifts.reduce((payload: any[], shift: any) => {
      const shiftPayload = buildShiftPayload(shift);

      if (shiftPayload) payload.push(shiftPayload);

      return payload;
    }, []);
  }

  function buildShiftPayload(shift: any) {
    if (!shift?.id) return shift;

    const originalShift = originalShifts.value.find((item) => item.id === shift.id);

    if (!originalShift) return shift;

    const changedFields = editableShiftFields.reduce((payload: any, field) => {
      if (isShiftFieldChanged(shift, originalShift, field)) payload[field] = shift[field];

      return payload;
    }, { id: shift.id });

    return Object.keys(changedFields).length > 1 ? changedFields : null;
  }

  function isShiftFieldChanged(shift: any, originalShift: any, field: string) {
    if (dateTimeShiftFields.includes(field)) {
      return !isSameDateTime(shift[field], originalShift[field]);
    }

    return shift[field] !== originalShift[field];
  }

  function isSameDateTime(value: any, originalValue: any) {
    if (!value && !originalValue) return true;
    if (!value || !originalValue) return false;

    return dayjs.utc(value).format("HH:mm") === dayjs.utc(originalValue).format("HH:mm");
  }

  function updateSavedShiftBaselines(payload: any[]) {
    payload.forEach((shiftPayload) => {
      if (!shiftPayload?.id) return;

      mergeShiftById(originalShifts.value, shiftPayload);
      mergeShiftById(currentShifts.value, shiftPayload);

      if (currentShift.value?.id === shiftPayload.id) {
        currentShift.value = { ...currentShift.value, ...shiftPayload };
      }

      removeChangedShiftId(shiftPayload.id);
    });
  }

  function mergeShiftById(shifts: any[], shiftPayload: any) {
    const shiftIndex = shifts.findIndex((shift) => shift.id === shiftPayload.id);

    if (shiftIndex === -1) return;

    shifts[shiftIndex] = { ...shifts[shiftIndex], ...shiftPayload };
  }

  function updateScheduleDelete(shift: any) {
    schedules.value.map((item) => {
      if (item.id === shift.employment) {
        item.shifts = item.shifts.filter((sh) => sh.id !== shift.id);
        refresh.value++;
      }
    });
  }

  function updateSchedulePack(shifts: any[]) {
    schedules.value.map((item) => {
      if (item.id === shifts[0].employment) {
        item.shifts = item.shifts.filter(shift => shift.id !== shifts[0].id); // remove old shifts from temp schedule
        item.shifts.push(...shifts);
        refresh.value++;
      }
    });
  }

  function setCurrentShift(shift: Shift | null) {
    currentShift.value = shift;
  }

  function setCurrentShifts(shifts: Shift[]) {
    currentShifts.value = shifts;
  }

  function updateCurrentShifts(shift: Shift) {
    const shiftId = shift.id;
    currentShifts.value = currentShifts.value.filter(
      (shift) => shift.id !== shiftId,
    );
    currentShifts.value.push(shift);
  }

  function setOriginalShifts(shifts: Shift[]) {
    originalShifts.value = shifts;
  }
  // add shift id in changedShifts if changes detected
  function addChangedShiftId(id: string) {
    if (!changedShifts.value.length || !changedShifts.value.includes(id))
      changedShifts.value.push(id);
  }

  function removeChangedShiftId(id: string) {
    changedShifts.value = changedShifts.value.filter((item) => item !== id);
  }

  function setCurrentDate(date: any) {
    currentScheduleDate.value = date;
    localStorage.setItem("date", JSON.stringify(date));
  }

  function getCurrentDate() {
    const dtjson = localStorage.getItem("date");
    if (dtjson) currentScheduleDate.value = dayjs(JSON.parse(dtjson));
    else currentScheduleDate.value = dayjs();
  }

  function nextScheduleMonth() {
    setCurrentDate(currentScheduleDate.value.add(1, "month"));
    fetchSchedule();
    fetchTimeSheet();
  }

  function prevScheduleMonth() {
    setCurrentDate(currentScheduleDate.value.subtract(1, "month"));
    fetchSchedule();
    fetchTimeSheet();
  }

  function goToMonth(year: number, month: number) {
    setCurrentDate(currentScheduleDate.value.year(year).month(month).startOf("month"));
    fetchSchedule();
    fetchTimeSheet();
  }

  function setCurrentDepartment(department: Department) {
    currentDepartment.value = department;
    localStorage.setItem("department", JSON.stringify(currentDepartment.value));
  }

  function getCurrentDepartment() {
    const depjson = localStorage.getItem("department");
    if (depjson) currentDepartment.value = JSON.parse(depjson);
    else currentDepartment.value = departmentStore.myDepartments[0];
  }

  function cutStr(str: string, len: number) {
    return str?.length > len ? str.slice(0, len) + "..." : str;
  }

  function addCurrentShift(shift: Shift) {
    currentShifts.value.push(shift);
  }

  function isExtraShift(shift: any) {
    return (
      shift.is_overtime ||
      shift.is_not_main_position ||
      shift.is_not_main_department ||
      shift.is_vacancy
    );
  }

  async function fetchReport(getReport: Function, reportName: string) {
    const data = {
      department_id: currentDepartment.value?.id,
      year: dayjs(currentScheduleDate.value).format("YYYY"),
      month: dayjs(currentScheduleDate.value).format("MM"),
    };
    try {
      const res = await getReport(data);
      const blob = new Blob([res.data]); // Get the response as a Blob
      const url = window.URL.createObjectURL(blob); // Create a URL for the Blob

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${reportName} за ${data.month}-${data.year}.xlsx`,
      ); // Set the desired filename and extension
      document.body.appendChild(link);
      link.click(); // Programmatically click the link to trigger download
      document.body.removeChild(link); // Clean up the link element
      window.URL.revokeObjectURL(url); // Release the object URL
    } catch (err: any) {
      return err;
    }
  }

  async function fetchTimesheetReport() {
    await fetchReport(getTimesheetReport, "Табель работы сотрудников");
  }

  async function fetchScheduleReport() {
    await fetchReport(getScheduleReport, "График работы сотрудников");
  }

  async function fetchConsolidatedReport() {
    await fetchReport(getConsolidatedReport, "Консолидированный отчет");
  }

  async function fetchFraudReport() {
    await fetchReport(getFraudReport, "Отчет о фроде");
  }

  async function fetchDisciplineReport() {
    await fetchReport(getDisciplineReport, "Отчет о дисциплине");
  }

  return {
    schedules,
    schedulesSortedByName,
    schedulesSortedByPosition,
    timesheets,
    positions,
    dayTypes,
    currentShift,
    currentScheduleDate,
    currentDepartment,
    refresh,
    users,
    shiftSchedules,
    selectedEmploymentSchedule,
    currentShifts,
    originalShifts,
    shiftHistory,
    isSaveShiftEnabled,
    isCurrentShiftChanged,
    changedShifts,
    prodCalendar,
    shiftFieldsClicked,
    stats,
    userStat,
    userStat2,
    isLightTheme,
    isCellHighlighting,
    isDayStatsVisible,
    isEmployeeStatsVisible,
    loading,
    rules,
    fetchSchedule,
    fetchTimeSheet,
    fetchPositions,
    fetchDayTypes,
    setCurrentShift,
    shiftPackCreateUpdate,
    checkCopiedShiftRules,
    nextScheduleMonth,
    prevScheduleMonth,
    goToMonth,
    fetchShiftSchedules,
    cutStr,
    fetchEmploymentSchedule,
    shiftDelete,
    addCurrentShift,
    setCurrentShifts,
    setOriginalShifts,
    isExtraShift,
    fetchTimesheetReport,
    fetchConsolidatedReport,
    fetchScheduleReport,
    fetchFraudReport,
    fetchDisciplineReport,
    fetchShiftHistory,
    getDayType,
    getPosition,
    addChangedShiftId,
    removeChangedShiftId,
    fetchProductionCalendar,
    scheduleCleanup,
    shiftValidate,
    handleSaveButtonAvailability,
    scheduleGenerate,
    updateCurrentShifts,
    fetchStats,
    getCurrentDate,
    setCurrentDepartment,
    getCurrentDepartment,
    sortSchedules
  };
});
